import type { Form10ClientParams } from './pdf-types.js';
import { formatPDFDate, type InspectionRow } from './pdf-layout.js';
import { renderForm10 } from './pdf-form10-layout.js';
export function createForm10PDF(data: Form10ClientParams) {
  const { company, equipment } = data;
  const examDateStr = formatPDFDate(data.examination_date);
  const rows: InspectionRow[] = [];
  const getValLines = (text: string) => [text || ''];
  const drawRow = (number: string, label: string, content: { textLines: string[]; isFormattedPoint3?: boolean; point3TypeHeader?: string }, _minHeight?: number) => {
    rows.push({ number, label, value: [content.point3TypeHeader, ...content.textLines].filter(Boolean).join('\n') });
  };
  // ── 8 Rows ────────────────────────────────────
  // Row 1: Name of Occupier of Factory
  const r1Val = getValLines(company?.occupier_name ?? '');
  drawRow('1', 'Name Of Occupier Of Factory', { textLines: r1Val }, 9.5);

  // Row 2: Address of Factory
  const r2Lines = [company?.company_name ?? '', company?.factory_address ?? ''].filter(Boolean);
  const r2Val = getValLines(r2Lines.join('\n'));
  drawRow('2', 'Address Of Factory', { textLines: r2Val }, 15);

  // Row 3: Distinguishing Number Or Mark...
  const typeHeader = equipment?.equipment_short_code ?? '';
  const swlPart = equipment?.swl_value
    ? `SWL: ${equipment.swl_value} Ton`
    : equipment?.swl_text
      ? `SWL: ${equipment.swl_text}`
      : '';
  const idAndSwlParts = [`ID No.: - ${equipment?.internal_id_number ?? ''}`, swlPart].filter(Boolean).join(', ');
  const specParts = [
    equipment?.lift_height ? `Lift Height ${equipment.lift_height}` : '',
    equipment?.make ? `Make: ${equipment.make}` : '',
    equipment?.model ? `Model: ${equipment.model}` : '',
    equipment?.equipment_size ? `Size: ${equipment.equipment_size}` : '',
    equipment?.location_in_factory ? `Location: ${equipment.location_in_factory}` : '',
  ].filter(Boolean).join(', ');
  const idAndSwlLine = specParts ? `${idAndSwlParts},` : idAndSwlParts;
  const r3TextLines: string[] = [idAndSwlLine];
  if (specParts) {
    r3TextLines.push(...getValLines(specParts));
  }
  drawRow(
    '3',
    'Distinguishing Number Or Mark (If Any) And Description Sufficient To Identify The Lifting Machine, Chain, Rope, Or The Lifting Tackle.',
    { textLines: r3TextLines, isFormattedPoint3: true, point3TypeHeader: typeHeader },
    20,
  );

  // Row 4: Date when first used
  let firstUseVal = equipment?.first_use_value ?? 'N/A';
  if (equipment?.first_use_type === 'EXACT_DATE' && equipment?.first_use_value) {
    try {
      firstUseVal = formatPDFDate(equipment.first_use_value);
    } catch {
      firstUseVal = equipment.first_use_value;
    }
  }
  const r4Val = getValLines(firstUseVal);
  drawRow('4', 'Date When The Lifting Machine, Chain, Rope Or Lifting Tackle Was First Used In The Factory.', { textLines: r4Val }, 11);

  // Row 5: Date of each examination
  const r5Val = getValLines(data.test_prefix_sentence);
  drawRow('5', 'Date Of Each Examination Made Under Section 29(1)(A)(iii) And By Whom It Was Carried Out.', { textLines: r5Val }, 15);

  // Row 6: Certificate relating to test
  const r6Val = getValLines(data.previous_inspection_details ?? 'Not Applicable');
  drawRow('6', 'Date And Number Of The Certificate Relating To Any Test And Examination Made Under Sub-rule (1) Of Rule 60 Together With The Name Of The Person Who Issued The Certificate.', { textLines: r6Val }, 16);

  // Row 7: Date of annealing
  const r7Val = getValLines(data.annealing_details ?? 'Not Applicable');
  drawRow('7', 'Date Of Annealing Or Other Heat Treatment If The Chain And Lifting Tackle Were Carried Out Under Sub-rule (5) Of Rule 60 And By Whom It Was Carried Out.', { textLines: r7Val }, 15);

  // Row 8: Particulars of defect
  let r8Text = '';
  if (data.defect_status === 'SAFE') {
    r8Text = data.defect_remarks?.trim() || 'Safe to use at SWL Capacity.';
  } else if (data.defect_status === 'NOT_APPLICABLE') {
    r8Text = 'Not Applicable';
  } else {
    r8Text = `REJECT — ${data.defect_remarks ?? ''}`;
  }
  const r8Val = getValLines(r8Text);
  drawRow('8', 'Particulars Of Any Defect Found At Any Such Examination Or After Annealing And Affecting The Safe Working Load And The Steps Taken To Remedy Such Defect.', { textLines: r8Val }, 18);

  return renderForm10(data, rows,
    `I / We certify that on ${examDateStr} I / We thoroughly examined the above-mentioned lifting machine/chain/rope/lifting tackle and that the above is a correct report of the result.`);
}
